import { useState } from "react";
import { toast } from "sonner";
import { KeyRound, Loader2, UserPlus } from "lucide-react";
import { api, brl, errMsg } from "@/lib/api";
import { useFetch } from "@/hooks/useFetch";
import { Field, inputCls } from "@/components/landing/ProposalForm";
import { AvatarUpload, ProfileForm } from "@/components/panel/PanelProfile";

const AddPhotographer = ({ done }) => {
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/panel/photographers", f);
      toast.success("Fotógrafo cadastrado. Envie o e-mail e a senha para ele.");
      setF({ name: "", email: "", password: "" });
      done();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="grid gap-4 rounded-[1.5rem] border border-white/10 bg-ink-2 p-6 sm:grid-cols-4" data-testid="add-photographer-form">
      <Field label="Nome"><input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inputCls} data-testid="add-ph-name-input" /></Field>
      <Field label="E-mail"><input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="add-ph-email-input" /></Field>
      <Field label="Senha inicial"><input required minLength={6} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className={inputCls} data-testid="add-ph-password-input" /></Field>
      <button disabled={busy} className="inline-flex items-center justify-center gap-2 self-end rounded-full bg-flash px-5 py-3 font-semibold text-ink disabled:opacity-60" data-testid="add-ph-submit-btn">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />} Cadastrar
      </button>
    </form>
  );
};

const Member = ({ u, reload }) => {
  const [open, setOpen] = useState(false);
  const patch = async (body, msg) => {
    try {
      await api.patch(`/panel/photographers/${u.id}`, body);
      toast.success(msg);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const resetPw = () => {
    const pw = window.prompt(`Nova senha para ${u.name} (mín. 6 caracteres):`);
    if (pw) patch({ password: pw }, "Senha alterada");
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-ink-2 p-5" data-testid={`team-row-${u.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-semibold">{u.name} {u.role === "admin" && <span className="ml-1 rounded-full bg-flash/15 px-2 py-0.5 text-xs text-flash">admin</span>} {!u.active && <span className="ml-1 rounded-full bg-red-400/15 px-2 py-0.5 text-xs text-red-300">inativo</span>}</p>
          <p className="text-sm text-haze">{u.email} · {u.listed ? "visível no site" : "oculto"} · reserva {u.booking_fee_cents > 0 ? brl(u.booking_fee_cents) : "só orçamento"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setOpen((o) => !o)} className="rounded-full border border-white/15 px-3 py-1.5 text-sm" data-testid={`team-edit-${u.id}`}>{open ? "Fechar" : "Editar perfil"}</button>
          <button onClick={resetPw} className="inline-flex items-center gap-1 rounded-full border border-white/15 px-3 py-1.5 text-sm" data-testid={`team-password-${u.id}`}><KeyRound className="h-4 w-4" />Senha</button>
          {u.role !== "admin" && <button onClick={() => patch({ active: !u.active }, u.active ? "Acesso desativado" : "Acesso reativado")} className="rounded-full border border-white/15 px-3 py-1.5 text-sm" data-testid={`team-toggle-${u.id}`}>{u.active ? "Desativar" : "Reativar"}</button>}
        </div>
      </div>
      {open && (
        <div className="mt-6 grid gap-6 lg:grid-cols-[180px_1fr]">
          <AvatarUpload userId={u.id} hasAvatar={!!u.avatar_path} url={`/panel/photographers/${u.id}/avatar`} testid={`team-avatar-${u.id}`} />
          <ProfileForm initial={u} onSave={async (profile) => { await api.patch(`/panel/photographers/${u.id}`, { profile }); reload(); }} />
        </div>
      )}
    </div>
  );
};

export const PanelTeam = () => {
  const { data, reload } = useFetch("/panel/photographers", []);
  const team = Array.isArray(data) ? data : [];
  return (
    <div className="space-y-6" data-testid="panel-team">
      <AddPhotographer done={reload} />
      <div className="grid gap-3">{team.map((u) => <Member key={u.id} u={u} reload={reload} />)}</div>
    </div>
  );
};
